import type { Dispute } from "@services/disputes/types";
import type { Movement } from "@services/movement-services/types";
import type { PortService } from "@services/port-services/types";
import type { FromToLocation } from "@services/vessel-call/types";

export const DEPARTURE_BLOCKER = {
  ACTIVE_MOVEMENT_SERVICES: "ACTIVE_MOVEMENT_SERVICES",
  OPEN_PORT_SERVICES: "OPEN_PORT_SERVICES",
  INVALID_DISPUTE_STATUS: "INVALID_DISPUTE_STATUS",
  VALIDATION_UNAVAILABLE: "VALIDATION_UNAVAILABLE",
} as const;

export type DepartureBlockerCode = (typeof DEPARTURE_BLOCKER)[keyof typeof DEPARTURE_BLOCKER];

export type DepartureBlocker = {
  code: DepartureBlockerCode;
  count: number;
  terminals?: string[];
  target: "movement-services" | "port-services" | "operations-disputes" | null;
};

export type DepartureAvailability = {
  isAvailable: boolean;
  blockers: DepartureBlocker[];
};

type AvailabilityInput = {
  currentMovements: Movement[];
  pastMovements: Movement[];
  portServices: PortService[];
  disputes: Dispute[];
  locations: FromToLocation[];
};

const PASSING_DISPUTE_STATUSES = new Set(["NO_DISPUTE", "RESOLVED"]);
const TERMINAL_PORT_SERVICE_STATUSES = new Set(["CLOSED", "CANCELLED", "REJECTED"]);
const EXCLUDED_TERMINALS = ["VOPAK", "ADNOC"];

const normalize = (value?: string | null) => value?.trim().toUpperCase() ?? "";

const isExcludedTerminal = (...values: Array<string | undefined>) =>
  values.some((value) =>
    EXCLUDED_TERMINALS.some((excluded) => normalize(value).includes(excluded)),
  );

export const evaluateDepartureAvailability = ({
  currentMovements,
  pastMovements,
  portServices,
  disputes,
  locations,
}: AvailabilityInput): DepartureAvailability => {
  const blockers: DepartureBlocker[] = [];

  if (currentMovements.length > 0) {
    blockers.push({
      code: DEPARTURE_BLOCKER.ACTIVE_MOVEMENT_SERVICES,
      count: currentMovements.length,
      target: "movement-services",
    });
  }

  const openPortServices = portServices.filter(
    (portService) => !TERMINAL_PORT_SERVICE_STATUSES.has(normalize(portService.status)),
  );
  if (openPortServices.length > 0) {
    blockers.push({
      code: DEPARTURE_BLOCKER.OPEN_PORT_SERVICES,
      count: openPortServices.length,
      target: "port-services",
    });
  }

  const terminalsByAlias = new Map<string, { code: string; name: string }>();
  locations
    .filter((location) => normalize(location.location) === "BERTH")
    .forEach((location) => {
      const terminal = {
        code: normalize(location.fromToLocationCode),
        name: location.locationName || location.fromToLocationCode,
      };
      terminalsByAlias.set(terminal.code, terminal);
      terminalsByAlias.set(normalize(location.locationName), terminal);
    });

  const applicableTerminals = new Map<string, { code: string; name: string }>();
  const addApplicableTerminal = (value?: string) => {
    const terminal = terminalsByAlias.get(normalize(value));
    if (!terminal || isExcludedTerminal(terminal.code, terminal.name)) return;
    applicableTerminals.set(terminal.code, terminal);
  };

  pastMovements
    .filter((movement) => normalize(movement.status) === "CLOSED")
    .forEach((movement) => addApplicableTerminal(movement.to));

  const disputesByMovementId = new Map<string, Dispute>();
  disputes.forEach((dispute) => {
    if (dispute.associatedMovementId) {
      disputesByMovementId.set(dispute.associatedMovementId, dispute);
    }
  });

  const invalidMovements = pastMovements
    .filter((movement) => normalize(movement.status) === "CLOSED")
    .map((movement) => {
      const terminal = terminalsByAlias.get(normalize(movement.to));
      if (!terminal || !applicableTerminals.has(terminal.code)) return null;

      const declaration = disputesByMovementId.get(movement.id);
      return declaration && PASSING_DISPUTE_STATUSES.has(declaration.status)
        ? null
        : { movement, terminal };
    })
    .filter((entry): entry is { movement: Movement; terminal: { code: string; name: string } } =>
      Boolean(entry),
    );

  if (invalidMovements.length > 0) {
    blockers.push({
      code: DEPARTURE_BLOCKER.INVALID_DISPUTE_STATUS,
      count: invalidMovements.length,
      terminals: [...new Set(invalidMovements.map(({ terminal }) => terminal.name))].sort(),
      target: "operations-disputes",
    });
  }

  return { isAvailable: blockers.length === 0, blockers };
};

export const unavailableDepartureAvailability = (): DepartureAvailability => ({
  isAvailable: false,
  blockers: [
    {
      code: DEPARTURE_BLOCKER.VALIDATION_UNAVAILABLE,
      count: 1,
      target: null,
    },
  ],
});
